import { AsyncPipe, TitleCasePipe } from '@angular/common';
import {
  Component,
  computed,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { Store } from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import {
  EMPTY,
  filter,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  takeUntil,
  throwError,
} from 'rxjs';
import { BtnTabComponent } from '../../components/buttons/btn-tab/btn-tab.component';
import { environment } from '../../../../environments/environment';
import { AppExceptionModel } from '../../../models/app-exception.model';
import { ProtestantCanonBooks } from '../../../models/books.bible.model';
import { Group } from '../../../models/group.model';
import { Member } from '../../../models/member.model';
import { MembershipStatus } from '../../../models/membership.status';
import {
  QuestionType,
  QuizQuestion,
} from '../../../models/question.quiz.model';
import { Quiz, QuizStatus } from '../../../models/quiz.model';
import { UserModel } from '../../../models/user.model';
import { MomentPipe } from '../../../pipes/moment.pipe';
import { QuizStatusPipe } from '../../../pipes/quizStatus.pipe';
import { QuizService } from '../../../services/quiz.service';
import { UtilsService } from '../../../services/utils.service';
import { AppState } from '../../../state/appstate';
import * as groupSelector from '../../../state/selectors/group.selector';
import { BtnActionComponent } from '../../components/buttons/btn-action/btn-action.component';
import { BtnBackComponent } from '../../components/buttons/btn-back/btn-back.component';
import { BtnFabComponent } from '../../components/buttons/btn-fab/btn-fab.component';
import { BtnMoreComponent } from '../../components/buttons/btn-more/btn-more.component';
import { BtnRouteComponent } from '../../components/buttons/btn-route/btn-route.component';
import {
  AlertType,
  DlgConfirmComponent,
} from '../../components/dialogs/dlg-confirm/dlg-confirm.component';
import { DlgInviteCodeComponent } from '../../components/dialogs/dlg-invite-code/dlg-invite-code.component';
import { DlgProgressComponent } from '../../components/dialogs/dlg-progress/dlg-progress.component';
import {
  ChipColor,
  ChipComponent,
} from '../../components/inputs/chip/chip.component';

@Component({
  selector: 'app-quiz-detail',
  standalone: true,
  imports: [
    BtnBackComponent,
    BtnFabComponent,
    ChipComponent,
    ReactiveFormsModule,
    AsyncPipe,
    TitleCasePipe,
    BtnRouteComponent,
    MomentPipe,
    BtnRouteComponent,
    BtnMoreComponent,
    BtnActionComponent,
    QuizStatusPipe,
    RouterLink,
    RouterOutlet,
    BtnTabComponent,
  ],
  templateUrl: './quiz-detail.component.html',
  styleUrl: './quiz-detail.component.css',
})
export class QuizDetailComponent implements OnInit, OnDestroy {
  showSearch = false;
  isMobile = false;

  store: Store<AppState> = inject(Store<AppState>);
  private dialog: MatDialog = inject(MatDialog);
  router: Router = inject(Router);
  fb: FormBuilder = inject(FormBuilder);
  activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private logger: NGXLogger = inject(NGXLogger);
  private quizService = inject(QuizService);
  private utilsService = inject(UtilsService);

  groupId: string | null = this.activatedRoute.snapshot.paramMap.get('id')!;
  quizId: string | null = this.activatedRoute.snapshot.paramMap.get('quizId')!;

  user: UserModel | null = null;
  group$: Observable<Group | null | undefined> = new Observable();
  quiz: Quiz | null = null;
  attempts$: Observable<Quiz[]> = of([]);
  students$: Observable<Member[]> = of([]);
  session$: Observable<any> = of(null);

  selectedQuestions = signal<QuizQuestion[]>([]);
  addQuestions: string[] = [];
  searchedQuestions$: Observable<QuizQuestion[]> = of([]);

  isLoading = signal(false);
  isWorking = signal(false);
  hasExistingAttempt = signal(false);
  progressDialogRef: MatDialogRef<DlgProgressComponent> | null = null;
  loadingAttempts$: Observable<boolean> = of(true);

  chipColor = ChipColor;
  quizStatus = QuizStatus;
  membershipStatus = MembershipStatus;
  protestantBooks = ProtestantCanonBooks;
  questionTypes = Object.values(QuestionType);

  totalScore = computed(() => {
    return this.selectedQuestions().reduce((acc, q) => acc + (q.score || 0), 0);
  });

  isWorking$: Observable<boolean> = of(false);

  myRoles$: Observable<any> = new Observable();
  exit$: Subject<void> = new Subject();
  showDropdown = signal(false);

  activeTab: string = 'details';

  constructor() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.exit$)
      )
      .subscribe(() => {
        this.setActiveTab();
      });
  }

  setActiveTab() {
    const url = this.router.url;
    if (url.includes('analytics')) {
      this.activeTab = 'analytics';
    } else {
      this.activeTab = 'details';
    }
  }

  isQuizExpired(): boolean {
    if (!this.quiz?.endDate) return false;
    const now = new Date();
    const endDate: any = this.quiz.endDate;
    const end = endDate.toDate ? endDate.toDate() : new Date(endDate);
    return end < now;
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  ngOnInit() {
    this.checkScreenSize();
    this.setActiveTab();

    this.activatedRoute.paramMap.pipe(take(1)).subscribe((params) => {
      this.groupId = params.get('id');
      this.quizId = params.get('quizId');
      if (this.quizId) {
        this.loadQuizDetails();
      }
    });

    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => {
        this.user = user;
      });

    this.group$ = this.store.select(groupSelector.getGroupById(this.groupId!));

    this.myRoles$ = this.store.select(
      groupSelector.getMyRolesInGroup(this.groupId),
    );

    this.loadAttempts();
  }

  loadQuizDetails() {
    if (!this.groupId || !this.quizId) {
      this.logger.error('Group ID or Quiz ID is missing in route parameters.');
      return;
    }
    this.quizService
      .getQuizById({ groupId: this.groupId, quizId: this.quizId })
      .subscribe({
        next: (quiz) => {
          this.quiz = quiz;
          this.myRoles$.pipe(take(1)).subscribe((roles) => {
            if (
              (roles.faculty && this.quiz?.createdById === this.user?.uid) ||
              roles.management
            ) {
              this.loadQuestions();
            }
          });
        },
        error: (err) => {
          this.logger.error('Error loading quiz details: ', err);
          this.utilsService.onShowAlert(
            {
              code: 'LOAD_QUIZ_ERROR',
              title: 'Error Loading Quiz',
              message: 'Failed to load quiz details. Please try again later.',
            } as AppExceptionModel,
            false,
          );
        },
      });
  }

  loadQuestions() {
    if (this.groupId && this.quiz?.questions) {
      const questionIds = this.quiz?.questions?.slice(
        this.selectedQuestions().length ?? 0,
        (this.selectedQuestions().length ?? 0) +
          (environment.production ? 10 : 2),
      );
      if (questionIds && questionIds.length > 0) {
        this.quizService
          .getQuizQuestions(this.groupId, questionIds)
          .then((qList) => {
            this.selectedQuestions.set([
              ...this.selectedQuestions(),
              ...(qList as QuizQuestion[]),
            ]);
          })
          .catch((error) => {
            this.logger.error('Error loading quiz questions: ', error);
            this.utilsService.onShowAlert(
              {
                code: 'LOAD_QUESTIONS_ERROR',
                title: 'Error Loading Questions',
                message:
                  'Failed to load quiz questions. Please try again later.',
              } as AppExceptionModel,
              false,
            );
          });
      }
    }
  }

  /**
   * Load quiz attempts by the user
   */
  loadAttempts() {
    if (!this.groupId || !this.quizId || !this.user?.uid) {
      return;
    }

    this.loadingAttempts$ = of(true);

    this.group$
      .pipe(
        take(1),
        switchMap((group) => {
          if (group?.memberStatus !== MembershipStatus.active) {
            this.loadingAttempts$ = of(false);
            return EMPTY;
          }

          // ← Extra safety layer
          try {
            return this.quizService.getQuizAttempts({
              groupId: this.groupId!,
              quizId: this.quizId!,
              userId: this.user?.uid!,
              memberType: group?.memberType ?? [], // ensure array
              limitRecords: 10, // explicit
            });
          } catch (err: any) {
            this.logger.error(
              'Sync error in getQuizAttempts call',
              JSON.stringify(err),
            );
            return throwError(() => err);
          }
        }),
      )
      .subscribe({
        next: (attempts) => {
          this.logger.debug(
            'Quiz attempts loaded successfully: ',
            attempts.length,
          );
          this.attempts$ = of(attempts);
          this.hasExistingAttempt.set(attempts.length > 0);
          this.loadingAttempts$ = of(false);
        },
        error: (error) => {
          this.logger.error(
            `Error loading quiz attempts: ${JSON.stringify(error, null, 2)}`,
          );

          this.utilsService.onShowAlert(
            {
              code: 'LOAD_ATTEMPTS_ERROR',
              title: 'Error Loading Attempts',
              message: `Failed to load your quiz attempts. Please try again later. Error: ${typeof error}, ${JSON.stringify(error['message'])}`,
            } as AppExceptionModel,
            false,
          );
          this.loadingAttempts$ = of(false);
        },
      });
  }

  @HostListener('window:resize')
  checkScreenSize() {
    this.isMobile = window.innerWidth < 768;
  }

  toggleSearch() {
    this.showSearch = !this.showSearch;
  }

  get animationParams() {
    return {
      value: 'visible',
      params: {
        startPos: this.isMobile ? 'translateY(100%)' : 'translateX(100%)',
      },
    };
  }

  toggleQuestion(q: any, addOrRemoveFlag: boolean) {
    if (addOrRemoveFlag) {
      this.addQuestion(q);
    } else {
      this.removeQuestion(q);
    }
  }

  addQuestion(q: any) {
    if (!this.selectedQuestions().find((item) => item.id === q.id)) {
      this.selectedQuestions.update((questions) => [...questions, q]);
    }
  }

  removeQuestion(q: any) {
    this.selectedQuestions.update((questions) =>
      questions.filter((item) => item.id !== q.id),
    );
  }

  isQuestionSelected(questionId: string): boolean {
    return this.selectedQuestions().some((q) => q.id === questionId);
  }

  copyInviteCode() {
    navigator.clipboard.writeText(this.quiz?.inviteCode!);
  }

  startQuiz() {
    if (this.quiz?.quizStatus === QuizStatus.cancelled) {
      this.utilsService.onShowAlert(
        {
          code: 'QUIZ_CANCELLED',
          title: 'Quiz Cancelled',
          message: 'This quiz has been cancelled and cannot be started.',
        } as AppExceptionModel,
        false,
      );
      return;
    }
    if (this.quiz?.quizStatus === QuizStatus.completed || this.isQuizExpired()) {
      this.utilsService.onShowAlert(
        {
          code: 'QUIZ_EXPIRED',
          title: 'Quiz Closed',
          message: 'This quiz has ended and is no longer accepting attempts.',
        } as AppExceptionModel,
        false,
      );
      return;
    }
    this.isWorking$ = of(true);
    this.dialog
      .open(DlgConfirmComponent, {
        data: {
          title: 'Quiz Instructions',
          description:
            '<span class="text-primary-500">You are about to start the quiz. Please read the instructions carefully before proceeding.</span><br><ul class="list-disc pl-5"><li>Once you start, the quiz timer will begin and cannot be paused.</li><li>Make sure you have a stable internet connection throughout the quiz.</li><li>Find a quiet environment to minimize distractions.</li><li>Do not refresh or close the browser during the quiz, as this may result in losing your progress.</li></ul>',
          action: 'Start',
          showType: AlertType.none,
        },
      })
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (confirmed) {
          this.handleStartQuiz();
        } else {
          this.isWorking$ = of(false);
        }
      });
  }

  handleStartQuiz() {
    if (this.quiz?.byInvite) {
      const dialogRef = this.dialog.open(DlgInviteCodeComponent, {
        data: { codeLength: 6 },
      });

      dialogRef.afterClosed().subscribe(async (result: string) => {
        if (result) {
          if (result === this.quiz?.inviteCode) {
            this.createAttempt();
          } else {
            this.isWorking$ = of(false);
            this.utilsService.onShowAlert(
              {
                code: 'INVALID_CODE',
                title: 'Invalid Code',
                message:
                  'The invite code you entered is incorrect. Please try again.',
              } as AppExceptionModel,
              false,
            );
          }
        } else {
          this.isWorking$ = of(false);
        }
      });
    } else {
      this.createAttempt();
    }
  }

  createAttempt() {
    this.logger.debug('Creating quiz attempt for quiz ID: ', this.quiz?.id);
    this.isWorking$ = of(true);
    this.showProgress('Preparing your quiz session...');

    this.quizService
      .generateAttemptDocument(
        this.quiz?.groupId!,
        this.quiz?.id!,
        { name: this.user?.name || this.user?.email || '', photoURL: this.user?.photoURL || '' }
      )
      .then((attemptId) => {
        this.showProgress('Quiz attempt created! Redirecting...');
        setTimeout(() => {
          this.hideProgress();
          this.isWorking$ = of(false);
          this.router.navigate(['active'], {
            relativeTo: this.activatedRoute,
            queryParams: { attemptId },
            replaceUrl: true,
          });
        }, 800);
      })
      .catch((error: any) => {
        this.logger.error('Error creating quiz attempt: ', error);
        this.hideProgress();
        this.isWorking$ = of(false);
        this.utilsService.onShowAlert(
          {
            code: 'CREATE_ATTEMPT_ERROR',
            title: 'Error Starting Quiz',
            message: `Failed to start the quiz. Please try again later. ${error}`,
          } as AppExceptionModel,
          false,
        );
      });
  }

  showProgress(message: string) {
    if (!this.progressDialogRef) {
      this.progressDialogRef = this.dialog.open(DlgProgressComponent, {
        data: { message },
        disableClose: true,
      });
    } else {
      this.progressDialogRef.componentInstance.message = message;
    }
  }

  hideProgress() {
    if (this.progressDialogRef) {
      this.progressDialogRef.close();
      this.progressDialogRef = null;
    }
  }

  toggleDropdown(event: Event) {
    event.stopPropagation();
    this.showDropdown.set(!this.showDropdown());
  }

  @HostListener('document:click')
  closeDropdown() {
    this.showDropdown.set(false);
  }

  hostLiveQuiz() {
    if (!this.quizId) return;
    this.showDropdown.set(false);
    this.router.navigate(['live'], { relativeTo: this.activatedRoute });
  }

  attemptOrJoinQuiz() {
    this.showDropdown.set(false);
    if (this.quiz?.isLive) {
      this.router.navigate(['participant'], { relativeTo: this.activatedRoute });
    } else {
      this.startQuiz();
    }
  }
}
